import { RJSFSchema, UiSchema } from '@rjsf/utils';
import axios from 'axios';

export const apiHost = import.meta.env.VITE_API_HOST;

export const url = (path: string) => {
  return `${apiHost}${path}`;
};

export const client = axios.create({
  withCredentials: true,
});

export type Patient = {
  patient_phone: string;
  patient_dob: string;
  patient_first_name: string;
  patient_last_name: string;
  patient_address_line_1: string;
  patient_address_line_2: string;
  patient_address_city: string;
  patient_address_state: string;
  patient_address_postal_code: string;
  patient_gender: string;
};

export type Pharmacy = {
  address_line_1: string;
  address_line_2: string;
  city: string;
  fax: string;
  name: string;
  npi: string;
  phone: string;
  receiving_pharmacist_name: string;
  state: string;
  zip: string;
};

type Coupon = {
  name: string;
  id: string;
  bin: string;
  pcn: string;
  group_id: string;
};

type RxTransfer = {
  coupon_json?: Coupon;
  created_at: string;
  fax_ids: string[];
  fax_number: string;
  fills: number;
  id: string;
  initiating_user: string;
  instructions: string;
  pharmacy_json: {
    address_line_1: string;
    address_line_2: string;
    city: string;
    fax: string;
    name: string;
    npi: string;
    phone: string;
    receiving_pharmacist_name: string;
    state: string;
    zip: string;
    pharmacy_type: string;
  };
  pharmacy_npi: string;
  prescription_id: string;
  quantity: string;
  row_id: number;
  transaction_period: {
    bounds: string;
    empty: boolean;
    lower: string;
    upper: string | null;
  };
  transferring_tech: string;
  channel: string;
};

type Rx = {
  drug_ndc: string;
  quantity: string;
  refills: number;
  days_supply: number;
  diagnosis_codes: string[];
  sig: string;
  pa_status?: string;
};

type TaskType = 'rx_transfer';

type Item = {
  created_at: string;
  form_schema: RJSFSchema;
  hold_until: string;
  id: string;
  owner: string;
  result: any;
  status: 'queued' | 'processing' | 'completed' | 'cancelled' | 'snoozed';
  status_updated_at: string;
  task_metadata: Record<string, unknown>;
  extra_context: string;
  task_type: TaskType;
  ui_schema: UiSchema;
};

export type ProviderPreferenceInfo = {
  benefits_preference: string;
  care_site: string | null;
  care_site_address: string | null;
  hcpcs: string;
  cpt: string;
  rendering_provider_npi: string | null;
  tin: string | null;
};

export type ItemApiResponse = {
  drx: DRX;
  item: Item;
  rx: Rx;
  transfer: RxTransfer;
  form_data: Record<string, unknown>;
  generic_call_info: GenericCallInfo;
  pa_info: PAInfo;
  copay_card_download_info: CopayCardDownloadInfo;
  insurance_info: InsuranceInfo;
  pa_info_section?: PAInfoSection;
  insurance_info_section?: InsuranceInfoSection;
  copay_card_info?: CopayCardInfo;
  provider_preference_info?: ProviderPreferenceInfo;
};

export type PAInfoSection = {
  pa_status?: string | null;
  additional_instruction?: string | null;
  pa_reference_number?: string | null;
  submitted_on?: string | null;
  approved_until?: string | null;
  pa_documents?: string[] | null;
  insurance_info?: InsuranceInfoSection | null;
};

export type InsuranceInfoSection = {
  insurance_cardholder_id?: string | null;
  insurance_bin?: string | null;
  insurance_pcn?: string | null;
  insurance_group_id?: string | null;
  insurance_name?: string | null;
  insurance_patient_first_name?: string | null;
  insurance_patient_last_name?: string | null;
  insurance_patient_zipcode?: string | null;
  insurance_document_ids?: string[] | null;
};

export type GenericCallInfo = {
  name: string;
  phone_number: string;
  generic_subtype: string;
};

export type InsuranceInfo = {
  cardholder_id: string | null;
  bin: string | null;
  pcn: string | null;
  group_id: string | null;
  name: string | null;
  patient_zipcode: string | null;
  patient_first_name: string | null;
  patient_last_name: string | null;
  insurance_document_ids: string[] | null;
};

export type CopayCardInfo = {
  cardholder_id?: string | null;
  bin?: string | null;
  pcn?: string | null;
  group?: string | null;
  drug?: string | null;
  retrieved_at?: string | null;
  marked_inactive?: boolean;
};

export type PAInfo = {
  external_plan_id: string | null;
  submission_mode: string | null;
  first_submitted_on_date: string | null;
  insurance_cardholder_id: string | null;
  submitted_fax_numbers: string[] | null;
  insurance_document_ids: string[] | null;
};

export type CopayCardDownloadInfo = {
  copay_card_url: string;
  gender: string;
  email_address: string;
  address_line_1: string;
  address_line_2: string;
  address_city: string;
  address_state: string;
  address_zip_code: string;
};

export type RX = {
  drug_ndc: string;
  quantity: string;
  sig: string;
  refills: number;
  pa_status?: string;
};

export type DRX = {
  drug_description: string;
  erx_id: number;
  patient_phone: string;
  prescriber_dea: string;
  prescriber_first_name: string;
  prescriber_last_name: string;
  prescriber_npi: string;
  prescriber_address_line_1: string;
  prescriber_address_line_2: string;
  prescriber_address_city: string;
  prescriber_address_state: string;
  prescriber_address_postal_code: string;
  prescriber_primary_phone: string;
  prescriber_fax?: string;
  practice_name?: string;
  office_tin?: string;
  provider_instructions?: string;
};
